import { useEffect, useState } from "react";
import moment from "moment";

export type CountdownParts = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** True while `target` is set and still in the future. */
  isActive: boolean;
  /** True once a set `target` has been reached (was future, now past). */
  isComplete: boolean;
};

const remainingMsFor = (target?: number | null): number => {
  if (typeof target !== "number") return 0;
  return Math.max(0, target - Date.now());
};

/**
 * Live countdown to an epoch-milliseconds `target`. Recomputes
 * `target - now` on every tick (never decrements a stored counter) so it
 * self-corrects after the tab is backgrounded, and stops ticking once the
 * target is reached. A null/undefined target yields an inactive countdown.
 */
export const useCountdown = (target?: number | null): CountdownParts => {
  const isSet = target != null;
  const [remaining, setRemaining] = useState<number>(() =>
    remainingMsFor(target),
  );

  useEffect(() => {
    setRemaining(remainingMsFor(target));
    // Nothing to tick for an unset or already-elapsed target.
    if (!isSet || remainingMsFor(target) <= 0) return;

    const id = setInterval(() => {
      const next = remainingMsFor(target);
      setRemaining(next);
      if (next <= 0) clearInterval(id);
    }, 1000);

    return () => clearInterval(id);
  }, [target]);

  const duration = moment.duration(remaining);

  return {
    // `asDays()` (total days) rather than `days()`, which caps at the
    // day-of-month component and rolls the rest into `.months()`.
    days: Math.floor(duration.asDays()),
    hours: duration.hours(),
    minutes: duration.minutes(),
    seconds: duration.seconds(),
    isActive: isSet && remaining > 0,
    isComplete: isSet && remaining <= 0,
  };
};
